import React, { useEffect, useState } from 'react'
import { Button } from '@/components/button'
import { useHasActiveSubscription } from '@/hooks/useHasActiveSubscription'
import { oauthCheckProductEligibility } from '@/services/checkout'
import ExternalRedeemModal from '@/pages/external/redeem/ExternalRedeemModal.tsx'
import { PremiumPaymentSession } from '@/pages/external/ExternalPremiumLogin/PremiumPaymentSession'
import { RevenueCatPurchase } from '@/pages/external/RevenuecatCheckout'
import { User } from '@/types'

export interface ProceedToRedeemContext {
  user: User
  token: string | null | undefined
  base64ClientKey: string
  eligiblePromotions: string[]
  apiError: boolean
}

interface StandardSuccessComponentProps {
  type: 'login' | 'payment' | 'signup'
  message?: string
  showProductEligibility?: boolean
  token?: string | null
  base64ClientKey: string
  user?: User
  customActions?: React.ReactNode
  /**
   * If provided, clicking the DUPR+ promo CTAs will fire this callback
   * (with eligibility context) instead of rendering the redeem flow inline.
   * Lets the parent render the redeem flow at a higher level so it can escape
   * any constrained-width layout the success card was rendered inside.
   */
  onProceedToRedeem?: (ctx: ProceedToRedeemContext) => void
}

/**
 * Unified success component that handles different success scenarios
 * and optionally shows DUPR+ eligibility promotion
 */
export const StandardSuccessComponent: React.FC<StandardSuccessComponentProps> = ({
  type,
  message,
  showProductEligibility = false,
  token,
  base64ClientKey,
  user,
  customActions,
  onProceedToRedeem,
}) => {
  const hasActiveSubscription = useHasActiveSubscription()
  const [eligiblePromotions, setEligiblePromotionIds] = useState<string[]>([])
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false)
  const [eligibilityChecked, setEligibilityChecked] = useState(false)
  const [proceedToRedeem, setProceedToRedeem] = useState(false)
  const [apiError, setApiError] = useState<boolean>(false)
  // Default messages based on type
  const getDefaultMessage = (type: string): string => {
    switch (type) {
      case 'login':
        return 'Successfully linked with DUPR+ account'
      case 'payment':
        return 'Your DUPR subscription is active, you may now close this window'
      case 'signup':
        return 'Account created successfully!'
      default:
        return 'Success!'
    }
  }

  const displayMessage = message || getDefaultMessage(type)

  // Check product eligibility on component mount
  useEffect(() => {
    const checkEligibility = async () => {
      if (!showProductEligibility || !token || !base64ClientKey || hasActiveSubscription || type === 'payment') {
        setEligibilityChecked(true)
        return
      }

      setIsCheckingEligibility(true)
      try {
        const products = await oauthCheckProductEligibility({
          token,
          request: { base64ClientKey: base64ClientKey }
        })
        setEligiblePromotionIds(products)
      } catch (error) {
        console.error('Failed to check product eligibility:', error)
        setEligiblePromotionIds([])
        setApiError(true)
      } finally {
        setIsCheckingEligibility(false)
        setEligibilityChecked(true)
      }
    }

    checkEligibility()
  }, [showProductEligibility, token, base64ClientKey, hasActiveSubscription, type])

  // Should show DUPR+ promotion
  const shouldShowPromotion =
    showProductEligibility &&
    eligibilityChecked &&
    !hasActiveSubscription &&
    type !== 'payment' &&
    eligiblePromotions.length >= 0 // Show even if no products (generic CTA)

  const handleProceed = () => {
    if (onProceedToRedeem && user) {
      onProceedToRedeem({ user, token, base64ClientKey, eligiblePromotions, apiError })
      return
    }
    setProceedToRedeem(true)
  }

  // Animated checkmark component
  const AnimatedCheckmark = () => (
    <div className="flex items-center justify-center">
      <svg
        className="w-16 h-16 text-green-500 animate-bounce"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          strokeWidth="2"
          className="opacity-25"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="3"
          d="M9 12l2 2 4-4"
          className="animate-pulse"
        />
      </svg>
    </div>
  )

  // DUPR+ icon component
  const DuprPlusIcon = () => (
    <svg
      className="w-5 h-5"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M12 2L3.09 8.26L4 10.24L12 6L20 10.24L20.91 8.26L12 2ZM12 22L3.09 15.74L4 13.76L12 18L20 13.76L20.91 15.74L12 22ZM12 10L20.91 15.74L20 17.76L12 12L4 17.76L3.09 15.74L12 10Z"/>
    </svg>
  )

  function getInitialSuccessComponent() {
    return (
      <div className="flex flex-col items-center gap-y-4 p-8 w-full">
        {/* Success Content - No background container since ExternalScreen provides it */}
          {/* Large Animated Checkmark */}
          <AnimatedCheckmark />

        {/* Success Message */}
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-900 mb-2">
            {type === 'login' ? 'You have successfully linked your DUPR account!' : displayMessage}
          </p>
        </div>

        {/* DUPR+ Promotion (when eligible) */}
        {shouldShowPromotion && !isCheckingEligibility && (
          <div className="text-center mt-4 pt-4 border-t border-gray-100 w-full">
              <div className="flex items-center justify-center gap-2 mb-2">
                <DuprPlusIcon />
                <h3 className="text-lg font-bold text-gray-900">
                  {apiError ? 'Get DUPR+!' : 'You\'re eligible for DUPR+!'}
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Get access to exclusive features, a verified player badge, and unlimited profile views.
              </p>

              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button
                  onClick={handleProceed}
                  className="bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center gap-2"
                >
                  <DuprPlusIcon />
                  Try DUPR+ Free
                </Button>
                <Button
                  onClick={handleProceed}
                  className="border-primary-500 text-primary-500 hover:bg-primary-50 font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Learn More
                </Button>
            </div>
          </div>
        )}

        {/* Loading state for eligibility check */}
        {isCheckingEligibility && showProductEligibility && (
          <div className="flex items-center gap-x-2 text-sm text-gray-500 mt-4">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
            <span>Checking eligibility...</span>
          </div>
        )}

        {/* Custom Actions */}
        {customActions && (
          <div className="w-full mt-4 pt-4 border-t border-gray-100">
            {customActions}
          </div>
        )}
      </div>)
  }

  // If proceeding to redeem and there was an API error, fallback to RevenueCat
  if (proceedToRedeem && user) {
    if (apiError || !eligiblePromotions.length) {
      // Fallback to RevenueCat when API failed or no promotions available
      return (
        <PremiumPaymentSession token={token || ''} base64ClientKey={base64ClientKey} user={user}>
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-[#05155E] from-15% via-[#0163D0] to-[#4B97FE] p-4">
            <div className="w-full max-w-md">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                {/* Header with DUPR branding */}
                <div className="bg-gradient-to-tr from-[#05155E] from-15% via-[#0163D0] to-[#4B97FE] p-9">
                  <p className="text-center text-white text-sm">Connect With</p>
                  <p className="text-center text-white text-[40px] font-black tracking-[-4px]">DUPR</p>
                  <p className="text-center text-white text-[17px] font-bold px-[30px]">
                    The world's most accurate pickleball rating
                  </p>
                </div>
                <div className="p-6">
                  <RevenueCatPurchase isModal={true} />
                </div>
              </div>
            </div>
          </div>
        </PremiumPaymentSession>
      )
    }
    return <ExternalRedeemModal token={token} base64ClientKey={base64ClientKey} promotionIds={eligiblePromotions} user={user} />
  }

  return getInitialSuccessComponent()

}

export default StandardSuccessComponent